import { React, useEffect, useState } from "react";
import AppWrapper from "../../AppWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import supabase from "../../../supabase-client";
import {
  Button,
  DropZone,
  FormLayout,
  Page,
  Select,
  TextField,
  Icon,
  Spinner,
} from "@shopify/polaris";
import { UploadIcon } from "@shopify/polaris-icons";
import { v4 as uuidv4 } from "uuid";

function EditCarModel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [model, setModel] = useState({
    name: undefined,
    show_as: undefined,
    brand: undefined,
    category: undefined,
    transmission: "Automática",
    show_on_website: false,
    passengers: undefined,
    doors: undefined,
    tank_capacity: undefined,
    fuel_type: undefined,
    image_url: undefined,
  });
  const [imageObject, setImageObject] = useState(undefined);

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const editingId = searchParams.get("id");

  const [saving, setSaving] = useState(false);
  const [didEdit, setDidEdit] = useState(false);

  const handleModelEdit = (value, id) => {
    setDidEdit(true);
    setModel({ ...model, [id]: value });
  };

  const saveModelDetails = async () => {
    setSaving(true);

    try {
      let imageUrl = model.image_url;

      // Upload ONLY if user dropped a new image
      if (imageObject) {
        let filename = "";

        if (!model.image_url) {
          // New image
          filename = uuidv4();

          const { error } = await supabase.storage
            .from("cars")
            .upload(filename, imageObject, { upsert: false });

          if (error) throw error;
        } else {
          // Replace existing image
          filename = model.image_url.split("/public/cars/")[1];

          const { error } = await supabase.storage
            .from("cars")
            .update(filename, imageObject, { upsert: true });

          if (error) throw error;
        }

        // Get public URL
        const { data } = supabase.storage.from("cars").getPublicUrl(filename);

        imageUrl = data.publicUrl;
      }

      const payload = {
        ...model,
        image_url: imageUrl,
      };

      if (editingId) {
        const { error } = await supabase
          .from("car_models")
          .update(payload)
          .eq("id", editingId);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("car_models").insert(payload);

        if (error) throw error;

        navigate("/cars/models");
      }

      setDidEdit(false);
    } catch (error) {
      console.error(error.message);
      return { success: false, error: error.message };
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("car_categories")
        .select("value:id, label:name");
      if (categoriesError) {
        console.error(categoriesError.message);
        return;
      }
      setCategories([
        { value: undefined, label: "Modelo", disabled: true },
        ...categoriesData,
      ]);

      const { data: brandsData, error: brandsError } = await supabase
        .from("car_brands")
        .select("value:id, label:name");
      if (brandsError) {
        console.error(brandsError);
        return;
      }
      setBrands([
        { value: undefined, label: "Marca", disabled: true },
        ...brandsData,
      ]);

      const { data: fuelData, error: fuelError } = await supabase
        .from("fuel_type")
        .select("value:id, label:name");
      if (fuelError) {
        console.error(fuelError);
        return;
      }
      setFuelTypes([
        { value: undefined, label: "Combustible", disabled: true },
        ...fuelData,
      ]);
    }

    fetchData();

    async function getModelInfo() {
      const { data, error } = await supabase
        .from("car_models")
        .select()
        .eq("id", editingId)
        .single();
      if (error) {
        console.error(error.message);
        return;
      }
      setModel(data);
    }

    if (editingId) {
      getModelInfo();
    }
  }, [editingId]);

  const handleImageDrop = async (e) => {
    const image = e[0];
    setImageObject(image);
  };

  const saveButtonDisabled = () => {
    const hasRequiredFields =
      model.name &&
      model.brand &&
      model.category &&
      model.passengers &&
      model.doors &&
      model.tank_capacity &&
      model.fuel_type;

    const hasImage = Boolean(model.image_url || imageObject);

    if (!hasRequiredFields || !hasImage) return true;

    if (editingId) {
      // Editing existing
      return !didEdit;
    }

    // Creating new
    return false;
  };

  const pageContent = () => {
    if (
      brands.length === 0 ||
      categories.length === 0 ||
      fuelTypes.length === 0
    )
      return <Spinner size="small" />;
    if (editingId && !model.name) return <Spinner size="small" />;

    const modelImageComponent = () => {
      if (imageObject) {
        const imageUrl = URL.createObjectURL(imageObject);

        return (
          <img
            src={imageUrl}
            alt={model.name}
            style={{ maxWidth: "100%", height: "auto" }}
          />
        );
      }
      if (model.image_url) {
        return (
          <img
            src={model.image_url}
            alt={model.name}
            style={{ maxWidth: "100%", height: "auto" }}
          />
        );
      }
      return <Icon source={UploadIcon} />;
    };

    return (
      <FormLayout>
        <div style={{ width: "200px", height: "200px", margin: "auto" }}>
          <DropZone
            allowMultiple={false}
            type="image"
            accept={[".png", ".webp", ".jpeg", ".jpg"]}
            onDropAccepted={handleImageDrop}
            variableHeight
            outline
            children={
              <div
                style={{
                  width: "200px",
                  height: "200px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {modelImageComponent()}
              </div>
            }
          />
        </div>

        <FormLayout.Group title="Información General">
          <TextField
            requiredIndicator
            label="Nombre"
            value={model.name}
            id="name"
            onChange={(value, id) => {
              handleModelEdit(value, id);
            }}
          />
          <TextField
            label="Apodo"
            value={model.show_as}
            id="show_as"
            onChange={(value, id) => {
              handleModelEdit(value, id);
            }}
          />
          <Select
            requiredIndicator
            label="Marca"
            value={model.brand}
            id="brand"
            onChange={(value, id) => {
              handleModelEdit(parseInt(value), id);
            }}
            options={brands}
          />
          <Select
            requiredIndicator
            label="Segmento"
            value={model.category}
            id="category"
            onChange={(value, id) => {
              handleModelEdit(parseInt(value), id);
            }}
            options={categories}
          />
          <Select
            requiredIndicator
            label="Transmisión"
            value={model.transmission}
            id="transmission"
            onChange={(value, id) => {
              handleModelEdit(value, id);
            }}
            options={["Automática", "Manual"]}
          />
          <Select
            label="Público"
            value={model.show_on_website}
            id="show_on_website"
            onChange={(value, id) => {
              handleModelEdit(value === "true", id);
            }}
            options={[
              { value: false, label: "No" },
              { value: true, label: "Si" },
            ]}
          />
        </FormLayout.Group>
        <FormLayout.Group title="Capacidades">
          <TextField
            requiredIndicator
            label="Pasajeros"
            value={model.passengers}
            id="passengers"
            onChange={(value, id) => {
              handleModelEdit(parseInt(value), id);
            }}
            type="number"
            min={0}
          />
          <TextField
            requiredIndicator
            label="Puertas"
            value={model.doors}
            id="doors"
            onChange={(value, id) => {
              handleModelEdit(parseInt(value), id);
            }}
            type="number"
            min={0}
          />
          <TextField
            label="Maletas Grandes"
            value={model.big_luggage}
            id="big_luggage"
            onChange={(value, id) => {
              handleModelEdit(parseInt(value), id);
            }}
            type="number"
            min={0}
          />
          <TextField
            label="Maletas Chicas"
            value={model.small_luggage}
            id="small_luggage"
            onChange={(value, id) => {
              handleModelEdit(parseInt(value), id);
            }}
            type="number"
            min={0}
          />
          <TextField
            label="Capacidad de Carga"
            value={model.weight_capacity}
            id="weight_capacity"
            onChange={(value, id) => {
              handleModelEdit(parseInt(value), id);
            }}
            type="number"
            min={0}
            suffix="kg"
          />
          <TextField
            requiredIndicator
            label="Capacidad de Tanque"
            value={model.tank_capacity}
            id="tank_capacity"
            onChange={(value, id) => {
              handleModelEdit(parseInt(value), id);
            }}
            type="number"
            min={0}
            suffix="L"
          />
          <Select
            requiredIndicator
            label="Tipo de Combustible"
            value={model.fuel_type}
            id="fuel_type"
            onChange={(value, id) => {
              handleModelEdit(parseInt(value), id);
            }}
            options={fuelTypes}
          />
        </FormLayout.Group>
      </FormLayout>
    );
  };

  return (
    <AppWrapper>
      <Page
        title={editingId ? "Editar Modelo" : "Crear Modelo"}
        backAction={{ content: "Modelos", url: "/cars/models" }}
        primaryAction={
          <Button
            variant="primary"
            onClick={saveModelDetails}
            loading={saving}
            disabled={saveButtonDisabled()}
          >
            Guardar
          </Button>
        }
      >
        {pageContent()}
      </Page>
    </AppWrapper>
  );
}

export default EditCarModel;
